import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage, ChatStatus, ChatSurface, ChatType, RelayMessage } from './types';
import { CHAT_TITLES } from './types';
import { openChatRelay, type ChatRelay } from './chatRelay';
import { holdNavigation } from '../../services/navigationHold';
import { mergeMessage } from './mergeMessage';
import { playNotificationSound } from './notificationSound';
import { showMessageNotification, windowIsUnattended } from './browserNotification';
import {
  createConnectChatSession,
  toChatMessage,
  type ChatDetails,
  type ChatSessionFactory,
  type ChatSessionHandle,
} from './sessionFactory';

// Owns the live Connect chat: the session handle, the transcript, and the relay to
// the popped-out window.
//
// The transcript lives in React state rather than in DOM nodes. That is the fix
// for WIPE-985: the previous implementation wrote messages straight into
// `#chat-messages` and bailed out of its onMessage handler when that element was
// absent, so popping out — which unmounted the panel — silently dropped every
// inbound message and left the agent talking to nobody.

let syntheticId = 0;
function nextSyntheticId(): string {
  syntheticId += 1;
  return `local-${syntheticId}`;
}

export interface StartChatRequest {
  chatType: ChatType;
  details: ChatDetails;
}

export interface UseChatSessionResult {
  status: ChatStatus;
  surface: ChatSurface;
  messages: ChatMessage[];
  chatType: ChatType | null;
  title: string;
  error: string | null;
  isLive: boolean;
  beginStart(): void;
  attach(request: StartChatRequest): Promise<void>;
  send(content: string): void;
  end(): void;
  popOut(): void;
  show(): void;
  hide(): void;
  reset(): void;
  setError(message: string | null): void;
}

export function useChatSession(
  sessionFactory: ChatSessionFactory = createConnectChatSession,
): UseChatSessionResult {
  const [status, setStatus] = useState<ChatStatus>('idle');
  const [surface, setSurface] = useState<ChatSurface>('panel');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatType, setChatType] = useState<ChatType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionRef = useRef<ChatSessionHandle | null>(null);
  const relayRef = useRef<ChatRelay | null>(null);
  // Read inside relay callbacks, which are registered once and would otherwise
  // close over a stale transcript.
  const messagesRef = useRef<ChatMessage[]>([]);
  const statusRef = useRef<ChatStatus>('idle');
  const surfaceRef = useRef<ChatSurface>('panel');
  const titleRef = useRef<string>('Chat');

  useEffect(() => {
    messagesRef.current = messages;
    statusRef.current = status;
    surfaceRef.current = surface;
  }, [messages, status, surface]);

  const appendMessage = useCallback((message: ChatMessage, options: { chime: boolean }) => {
    setMessages((prev) => mergeMessage(prev, message));
    // Relay before any DOM concern. This ordering is the regression guard for
    // WIPE-985: the popped-out window must receive the message whether or not
    // this tab is currently rendering the panel.
    relayRef.current?.post({ type: 'message', message });
    if (!options.chime) return;
    playNotificationSound();
    // A popped-out window notifies for itself, so it alone knows whether its own
    // view is in front of the user. A minimized chat is out of sight even with this
    // tab focused, and clicking the notification brings it back.
    if (surfaceRef.current === 'popup') return;
    if (surfaceRef.current === 'hidden' || windowIsUnattended()) {
      showMessageNotification(message, titleRef.current, () => setSurface('panel'));
    }
  }, []);

  const addSystemMessage = useCallback((content: string) => {
    appendMessage(
      { id: nextSyntheticId(), kind: 'system', sender: 'System', content, at: new Date().toISOString() },
      { chime: false },
    );
  }, [appendMessage]);

  const markEnded = useCallback((notice: string) => {
    // Terminal for the session, but the transcript stays readable and
    // downloadable until the user dismisses it.
    setStatus((prev) => (prev === 'ended' ? prev : 'ended'));
    addSystemMessage(notice);
    relayRef.current?.post({ type: 'ended' });
  }, [addSystemMessage]);

  const send = useCallback((content: string) => {
    const trimmed = content.trim();
    if (!trimmed) return;
    const session = sessionRef.current;
    if (!session) return;

    // Echo optimistically so typing feels responsive. Connect delivers this
    // message back to us as a transcript item; the echo is marked pending so
    // mergeMessage reconciles the two instead of rendering both.
    appendMessage(
      { id: nextSyntheticId(), kind: 'customer', sender: 'You', content: trimmed, at: new Date().toISOString(), pending: true },
      { chime: false },
    );

    try {
      void Promise.resolve(session.sendMessage({ contentType: 'text/plain', message: trimmed }))
        ?.catch?.((err: unknown) => {
          console.error('Failed to send chat message:', err);
          addSystemMessage('That message may not have been delivered.');
        });
    } catch (err) {
      console.error('Failed to send chat message:', err);
      addSystemMessage('That message may not have been delivered.');
    }
  }, [appendMessage, addSystemMessage]);

  const end = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    void session.disconnectParticipant().catch((err: unknown) => {
      console.log('Error disconnecting participant:', err);
    });
    markEnded('You ended the chat');
  }, [markEnded]);

  // Enter the starting state before the caller's start-chat request, so the UI
  // acknowledges the click immediately. That request opens a Connect contact and can
  // take seconds, and QA saw several seconds of nothing happening because the status
  // only changed once it came back.
  const beginStart = useCallback(() => {
    setStatus('starting');
    setSurface('panel');
    setError(null);
    setMessages([]);
  }, []);

  const attach = useCallback(async ({ chatType: type, details }: StartChatRequest) => {
    setStatus('starting');
    setSurface('panel');
    setError(null);
    setMessages([]);
    setChatType(type);
    titleRef.current = CHAT_TITLES[type];

    let session: ChatSessionHandle;
    try {
      session = sessionFactory(details);
    } catch (err) {
      console.error('Error initializing chat widget:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize chat widget');
      setStatus('error');
      return;
    }

    session.onMessage((event) => {
      const message = toChatMessage(event, nextSyntheticId);
      if (!message) return;
      // Chime only for the other party; the local echo already happened on send.
      appendMessage(message, { chime: message.kind !== 'customer' });
    });
    session.onEnded(() => markEnded('Chat has ended'));
    session.onConnectionBroken(() => markEnded('Chat has ended'));

    try {
      await session.connect();
    } catch (err) {
      console.error('Failed to connect to chat:', err);
      setError(`Failed to connect to chat: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStatus('error');
      return;
    }

    sessionRef.current = session;
    setStatus('open');
  }, [sessionFactory, appendMessage, markEnded]);

  // Errors are terminal for a start attempt, so they carry the status with them.
  // Reported separately from attach() because the widget's own preconditions and
  // its start-chat call both fail before there is a session to attach.
  const fail = useCallback((message: string | null) => {
    setError(message);
    setStatus((prev) => (message ? 'error' : prev === 'error' ? 'idle' : prev));
  }, []);

  const reset = useCallback(() => {
    sessionRef.current = null;
    setStatus('idle');
    setSurface('panel');
    setMessages([]);
    setChatType(null);
    setError(null);
  }, []);

  // A chat is live from the moment it is connected until it ends, regardless of
  // which window is rendering it. Guarding the launch buttons on this is what
  // stops a popped-out chat from being orphaned by starting a second one.
  const isLive = status === 'open' || status === 'starting';

  // Hold off the app's own navigations for as long as that is true. The session is held here,
  // in the tab that opened it, so this covers a popped-out chat too: reloading this tab would
  // end the conversation the other window is displaying.
  useEffect(() => {
    if (!isLive) return;
    return holdNavigation();
  }, [isLive]);

  // The panel keeps rendering until the new window says it is ready (the
  // popup-ready handler below moves the surface). Moving it here instead made the
  // transcript vanish for as long as the window took to boot and load its chunk,
  // which reads as the conversation having been lost. Overlapping for a moment is
  // the better failure: a blocked or slow pop-up leaves the chat on screen rather
  // than nowhere.
  const popOut = useCallback(() => {
    window.open(`/chat-popup?${new URLSearchParams({ title: titleRef.current })}`, 'chat-popup', 'width=420,height=600,resizable=yes,scrollbars=no');
  }, []);

  const show = useCallback(() => setSurface('panel'), []);

  // The panel's ✕ minimizes: the contact stays connected and "Return to Chat"
  // brings it back. Once the chat has ended there is nothing to return to, so the
  // same gesture discards the transcript instead of hiding it out of reach.
  const hide = useCallback(() => {
    if (statusRef.current === 'open' || statusRef.current === 'starting') {
      setSurface('hidden');
      return;
    }
    reset();
  }, [reset]);

  // One relay for the lifetime of the provider. Registered once so no inbound
  // message can arrive while the channel is being torn down and rebuilt.
  useEffect(() => {
    const relay = openChatRelay((message: RelayMessage) => {
      // Every open tab of the app has a provider on this channel, but only the one
      // holding the session may answer. A chatless tab used to reply to
      // popup-ready with its empty transcript, and that reply raced — and usually
      // beat — the real one, so the pop-out came up with no messages even though
      // its channel was working and anything typed in it appeared.
      if (!sessionRef.current) return;

      switch (message.type) {
        case 'popup-ready':
          // Replay the full transcript every time a popup announces itself, so
          // closing and re-popping shows the conversation rather than a blank
          // window. Sent unconditionally — the popup ignores duplicates.
          relay.post({
            type: 'history',
            messages: messagesRef.current,
            title: titleRef.current,
            ended: statusRef.current === 'ended',
          });
          setSurface('popup');
          break;
        case 'popup-closed':
          // Closing the pop-out window jumps the conversation back into the page
          // rather than ending it: the session never left this tab, so there is
          // nothing to tear down and the jump keeps it rendered somewhere.
          setSurface('panel');
          break;
        case 'send':
          send(message.content);
          break;
        case 'end':
          end();
          break;
        default:
          break;
      }
    });
    relayRef.current = relay;
    return () => {
      relayRef.current = null;
      relay.close();
    };
  }, [send, end]);

  return {
    status,
    surface,
    messages,
    chatType,
    title: chatType ? CHAT_TITLES[chatType] : 'Chat',
    error,
    isLive,
    beginStart,
    attach,
    send,
    end,
    popOut,
    show,
    hide,
    reset,
    setError: fail,
  };
}
